import { invariant } from './invariant';

/**
 * Money as delivered by the API: integer minor units (cents, paise) plus an ISO-4217 code.
 * `amount` is never a decimal string or a float; the helpers below convert to and from major
 * units at the edges (inputs, display). Formatting lives in @nucleus-commerce/i18n.
 */
export interface Money {
  amount: number;
  currency: string;
}

/** Currencies whose minor-unit digits must not depend on the runtime's ICU data. */
const KNOWN_DECIMALS: Readonly<Record<string, number>> = {
  JPY: 0,
  KRW: 0,
  VND: 0,
  CLP: 0,
  ISK: 0,
  BHD: 3,
  KWD: 3,
  OMR: 3,
  JOD: 3,
  IQD: 3,
  TND: 3,
  LYD: 3,
};

const decimalsCache = new Map<string, number>(Object.entries(KNOWN_DECIMALS));

/** Number of minor-unit digits for a currency (2 for USD, 0 for JPY, 3 for KWD). */
export function currencyDecimals(currency: string): number {
  const code = currency.toUpperCase();
  const cached = decimalsCache.get(code);
  if (cached !== undefined) return cached;
  let decimals: number;
  try {
    decimals =
      new Intl.NumberFormat('en', { style: 'currency', currency: code }).resolvedOptions()
        .maximumFractionDigits ?? 2;
  } catch {
    decimals = 2;
  }
  decimalsCache.set(code, decimals);
  return decimals;
}

function assertInteger(value: number): void {
  invariant(Number.isSafeInteger(value), `Money amount must be a safe integer, got ${value}`);
}

// Math.round sends -0.5 to -0, so round the magnitude and restore the sign.
function roundHalfUp(value: number): number {
  return Math.sign(value) * Math.round(Math.abs(value));
}

/** Builds a Money value from minor units: `money(1299, 'USD')` is $12.99. */
export function money(amount: number, currency: string): Money {
  assertInteger(amount);
  return { amount, currency };
}

export function zeroMoney(currency: string): Money {
  return { amount: 0, currency };
}

export function isMoney(value: unknown): value is Money {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.amount === 'number' && Number.isSafeInteger(v.amount) && typeof v.currency === 'string'
  );
}

/**
 * Major units (what a person types or reads, "12.99") to Money. Accepts a number or a plain
 * decimal string; digits beyond the currency's decimals are rounded half-up.
 */
export function fromMajor(major: number | string, currency: string): Money {
  const decimals = currencyDecimals(currency);
  const text = typeof major === 'number' ? major.toString() : major.trim();
  invariant(/^-?\d*(?:\.\d*)?$/.test(text) && /\d/.test(text), `Not a decimal amount: ${major}`);
  const negative = text.startsWith('-');
  const [intPart = '', fracPart = ''] = text.replace('-', '').split('.');
  let amount = Number(`${intPart || '0'}${fracPart.slice(0, decimals).padEnd(decimals, '0')}`);
  const dropped = fracPart.slice(decimals);
  if (dropped !== '' && Number(dropped[0]) >= 5) amount += 1;
  assertInteger(amount);
  return { amount: negative ? -amount : amount, currency };
}

/** Money to a plain decimal string in major units: `{ 1299, USD }` → "12.99". Exact. */
export function toMajorString(m: Money): string {
  const decimals = currencyDecimals(m.currency);
  const digits = Math.abs(m.amount)
    .toString()
    .padStart(decimals + 1, '0');
  const intPart = digits.slice(0, digits.length - decimals);
  const fracPart = digits.slice(digits.length - decimals);
  const sign = m.amount < 0 ? '-' : '';
  return decimals === 0 ? `${sign}${intPart}` : `${sign}${intPart}.${fracPart}`;
}

function assertSameCurrency(a: Money, b: Money): void {
  invariant(a.currency === b.currency, `Currency mismatch: ${a.currency} vs ${b.currency}`);
}

export function addMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amount + b.amount, a.currency);
}

export function subtractMoney(a: Money, b: Money): Money {
  assertSameCurrency(a, b);
  return money(a.amount - b.amount, a.currency);
}

/** Multiplies by a factor (quantity, rate) and rounds half-up to whole minor units. */
export function multiplyMoney(m: Money, factor: number): Money {
  return money(roundHalfUp(m.amount * factor), m.currency);
}

export function negateMoney(m: Money): Money {
  return money(-m.amount, m.currency);
}

export function sumMoney(items: readonly Money[], currency: string): Money {
  return items.reduce<Money>((acc, item) => addMoney(acc, item), zeroMoney(currency));
}

/** -1 if a < b, 0 if equal, 1 if a > b. */
export function compareMoney(a: Money, b: Money): -1 | 0 | 1 {
  assertSameCurrency(a, b);
  return a.amount < b.amount ? -1 : a.amount > b.amount ? 1 : 0;
}

export function isZeroMoney(m: Money): boolean {
  return m.amount === 0;
}

export function isNegativeMoney(m: Money): boolean {
  return m.amount < 0;
}

/**
 * Parses what a user typed into a price field, in major units. Accepts "1299", "1,299.50",
 * " $1299.5 ", "-10". Returns null when the input is not a plain decimal number. Locale-specific
 * decimal commas are intentionally not guessed; the form layer presents a numeric input.
 */
export function parseMoneyInput(input: string, currency: string): Money | null {
  const cleaned = input.replace(/[^\d.-]/g, '');
  if (!/^-?\d*(?:\.\d*)?$/.test(cleaned) || !/\d/.test(cleaned)) return null;
  try {
    return fromMajor(cleaned, currency);
  } catch {
    return null;
  }
}

/** Major units as a float, for charts and sliders only. Never feed it back into arithmetic. */
export function moneyToDisplayNumber(m: Money): number {
  return m.amount / 10 ** currencyDecimals(m.currency);
}
